import { ApolloError } from "@apollo/client";

const ABORTED_MESSAGE = "signal is aborted";

/**
 * Structured error response from formatError utility.
 */
export interface ErrorResponse {
  message?: string;
  errorCode?: string | number;
  errorStatusCode?: string | number;
  aborted?: boolean;
}

/**
 * GraphQL error extensions containing additional error information.
 */
interface GraphQLErrorExtensions {
  code?: string | number;
  originalError?: {
    message?: string | string[];
    statusCode?: string | number;
  };
}

/**
 * Detects an error that represents a request the client cancelled itself
 * rather than a genuine failure.
 * @remarks
 * The common source is `apolloClient.resetStore()` aborting in-flight
 * queries on an active-team POV switch; a superseded typeahead request is
 * another. These surface as a `DOMException` named `AbortError` (message
 * `"signal is aborted without reason"`), sometimes wrapped as the
 * `networkError` of an `ApolloError`. They must not be shown to the user.
 * @param error - A network error, DOMException, or wrapped ApolloError
 * @returns True when the error is a self-cancelled (aborted) request
 */
export const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  if ((error as { name?: string }).name === "AbortError") {
    return true;
  }
  const message = (error as { message?: string }).message;
  if (typeof message === "string" && message.includes(ABORTED_MESSAGE)) {
    return true;
  }
  const networkError = (error as ApolloError).networkError;
  return networkError && networkError !== error
    ? isAbortError(networkError)
    : false;
};

/**
 * Detects an error that represents a request the client gave up on after
 * exceeding its own timeout budget, as distinct from a self-cancelled abort.
 * @remarks
 * The client arms a timeout (see `createTimeoutFetch` in
 * `providers/ApolloClient.tsx`) and, when it fires, rejects the fetch with an
 * `Error` whose `name` is `"TimeoutError"`. Unlike an intentional
 * `AbortError` (which the errorLink swallows so no toast appears, SE-4940), a
 * timeout is a genuine failure: it must propagate so the UI can surface an
 * error + retry affordance (SE-5055). The error may arrive wrapped as the
 * `networkError` of an `ApolloError`, so this unwraps recursively.
 * @param error - A network error, Error, or wrapped ApolloError
 * @returns True when the error is a client-side request timeout
 */
export const isTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  if ((error as { name?: string }).name === "TimeoutError") {
    return true;
  }
  const networkError = (error as ApolloError).networkError;
  return networkError && networkError !== error
    ? isTimeoutError(networkError)
    : false;
};

/**
 * Extracts a displayable message from a failure that never produced a GraphQL
 * payload — a request timeout, a dropped connection, or a plain `Error` or
 * `TypeError` thrown inside the promise chain.
 * @remarks
 * SE-6889. Before this existed, `formatError` read `graphQLErrors[0]` and
 * nothing else, so all three of those causes returned `message: undefined` and
 * every caller rendered its own `?? "Something went wrong"`. Three unrelated
 * failures became one sentence, and the sign-in screen could not tell a user
 * whose code was never sent from a user whose request never left the device.
 *
 * Two cases stay deliberately silent, because a message here is worse than
 * none:
 * - a self-cancelled request (`AbortError`), whose runtime text is "signal is
 *   aborted without reason". It is not a failure the user caused or can act
 *   on — SE-4940 swallows it in the errorLink — and `aborted: true` already
 *   carries it. Surfacing it would put an internal string in a toast.
 * - a blank message, which would render an empty error region or an empty
 *   toast. Returning `undefined` instead hands the decision back to the
 *   caller's own fallback copy.
 * @param error - The error to inspect, wrapped as an ApolloError or bare.
 * @returns The transport-level message, or undefined when there is none worth showing.
 */
/**
 * Treats a blank string as no message at all.
 * @remarks
 * SE-6889, second pass. `nonGraphQLMessage` already screened blanks and said
 * why; the GraphQL arms did not, so a backend payload carrying `message: ""`
 * travelled all the way to the UI and rendered an EMPTY error region and an
 * EMPTY toast. That is this ticket's own symptom — a failure that says
 * nothing — reintroduced through the fix for it.
 *
 * Screening here rather than at each call site makes the guard uniform for
 * every `formatError` consumer, of which there are ~100, all of which write
 * `formatError(e)?.message ?? "<their own fallback>"` and all of which were
 * therefore exposed.
 *
 * Trimmed emptiness, not a wider `||`: the thing being detected is "blank",
 * not "falsy". `||` would also swallow a legitimately falsy-but-meaningful
 * value if one ever existed here.
 * @param message - A candidate message from any arm.
 * @returns The message, or undefined when it carries no visible characters.
 */
const presentMessage = (message: unknown): string | undefined =>
  typeof message === "string" && message.trim().length > 0
    ? message
    : undefined;

const nonGraphQLMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object" || isAbortError(error)) {
    return undefined;
  }

  const networkError = (error as ApolloError).networkError;
  const source =
    networkError && networkError !== error ? networkError : (error as Error);
  return presentMessage((source as { message?: unknown }).message);
};

/**
 * Formats Apollo GraphQL errors into a structured error response.
 * @remarks
 * Extracts error details from Apollo GraphQL errors, including messages,
 * error codes, status codes, and network errors. Handles both single
 * and multiple error messages from the backend.
 *
 * Message precedence, highest first: the nested `originalError.message` the
 * backend attaches, then the GraphQL error's own message, then — SE-6889 — the
 * transport-level message for failures that never reached GraphQL at all. The
 * first two are unchanged; the third only fills what used to be `undefined`.
 * @param error - The error to format (typically an ApolloError)
 * @returns Structured error response with extracted details
 */
export const formatError = (error: unknown): ErrorResponse => {
  const graphQLErrors = (error as ApolloError)?.graphQLErrors?.[0] ?? undefined;
  const extensions =
    (graphQLErrors?.extensions as GraphQLErrorExtensions | undefined) ??
    undefined;
  const originalError = extensions?.originalError ?? undefined;

  const errorResponse: ErrorResponse = {
    message:
      presentMessage(
        Array.isArray(originalError?.message)
          ? originalError?.message?.[0]
          : originalError?.message
      ) ??
      presentMessage(graphQLErrors?.message) ??
      nonGraphQLMessage(error) ??
      undefined,
    errorCode: extensions?.code ?? undefined,
    errorStatusCode: originalError?.statusCode ?? undefined,
    aborted: isAbortError(error),
  };

  return errorResponse;
};
